import { ArrowPathIcon } from '@heroicons/react/16/solid';
import { ButtonHTMLAttributes, forwardRef, ReactNode } from 'react';

interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  id?: string;
  type: 'button' | 'submit';
  variant?: keyof typeof styles;
  disabled?: boolean;
  className?: string;
  onClick?: React.MouseEventHandler<HTMLButtonElement>;
  children: ReactNode;
  name?: string;
  value?: string;
  loading?: boolean;
}

const styles = {
  primary:
    'rounded bg-primary px-2 py-1 text-xs font-semibold text-white shadow-sm hover:bg-secondary focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-secondary disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors ease-in-out duration-200',
  outline:
    'flex items-center justify-center gap-3 rounded-md bg-white px-3 py-2 text-sm font-semibold text-primary shadow-sm ring-1 ring-inset ring-primary hover:text-secondary hover:ring-secondary focus-visible:ring-transparent disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors ease-in-out duration-200',
  danger:
    'rounded bg-red-500 px-2 py-1 text-xs font-semibold text-white shadow-sm hover:bg-[#FF4757] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#FF4757] disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors ease-in-out duration-200',
  success:
    'rounded bg-green-500 px-2 py-1 text-xs font-semibold text-green-600 shadow-sm hover:bg-primary focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors ease-in-out duration-200',
  warning:
    'rounded bg-orange-500 px-2 py-1 text-xs font-semibold text-orange-600 shadow-sm focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors ease-in-out duration-200',
  accordion:
    'w-full flex justify-between items-center p-4 bg-gray-100 hover:bg-gray-200 transition-colors',
  createDrillDown:
    'w-full inline-flex items-center justify-center gap-x-1.5 rounded-md bg-primary px-2.5 py-3 text-sm font-semibold text-white shadow-xs hover:bg-secondary focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-secondary transition-colors ease-in-out duration-200',
  closeDrillDown:
    'flex justify-center w-full px-2 py-3 text-gray-600 hover:[#CC2936]',
  icon: 'flex items-center justify-center p-1 rounded hover:bg-gray-200 w-min',
  link: 'text-primary hover:underline hover:text-secondary font-semibold transition-colors ease-in-out duration-200',
};

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(
  (
    {
      type = 'button',
      id,
      variant = 'primary',
      disabled,
      className = '',
      onClick,
      children,
      name,
      value,
      loading,
      ...props
    },
    ref
  ) => (
    <button
      ref={ref}
      data-testid={id}
      type={type}
      disabled={loading || disabled}
      className={`${styles[variant]} ${className}`}
      onClick={onClick}
      name={name}
      value={value}
      {...props}
    >
      {loading ? (
        <span className="flex w-full items-center justify-center">
          <ArrowPathIcon className="size-5 animate-spin text-gray-900" />
        </span>
      ) : (
        <>{children}</>
      )}
    </button>
  )
);
